import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import type { GameDTO } from '@pickem/shared';
import { useApi } from '../lib/useApi';
import { Spinner, ErrorBanner, EmptyState, SectionTitle } from '../components/ui';
import { ordinal } from '../lib/format';
import { useAuth } from '../lib/auth';

/** How often to refetch the live leaderboard while a week is in progress. */
const LIVE_REFRESH_MS = 30_000;

interface ResultRow {
  playerId: string;
  name: string;
  correctPicks: number;
  gamesPicked: number;
  tiebreakerPrediction: number | null;
  actualTiebreaker: number | null;
  tiebreakerDifference: number | null;
  placement: number | null;
  leaguePoints: number;
  bonusPoints: number;
  picks: { gameId: string; selectedTeamId: string; correct: boolean | null }[];
}

interface ResultsData {
  week: { id: string; weekNumber: number; displayName: string; calcStatus?: string } | null;
  locked?: boolean;
  provisional?: boolean;
  games?: GameDTO[];
  rows?: ResultRow[];
}

export function WeeklyResultsPage() {
  // No :weekId → current-week mode (the shareable /results link).
  const { weekId } = useParams();
  const path = weekId ? `/weeks/${weekId}/results` : '/weeks/current/results';
  const { data, error, loading, reload } = useApi<ResultsData>(path);
  const { user } = useAuth();

  // While the week is locked but not yet finalized, placements move as scores
  // land — auto-refresh so the leaderboard stays live without a manual reload.
  const isLive = !!data?.locked && data?.week?.calcStatus !== 'CALCULATED';
  useEffect(() => {
    if (!isLive) return;
    const id = window.setInterval(reload, LIVE_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [isLive, reload]);

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} />;
  if (!data) return null;

  if (!data.week) {
    return (
      <div>
        <SectionTitle>Results</SectionTitle>
        <EmptyState title="No current week">
          There’s no active week to show results for yet. Check back on game day.
        </EmptyState>
      </div>
    );
  }

  if (!data.locked) {
    return (
      <div>
        <SectionTitle>{data.week.displayName} Results</SectionTitle>
        <EmptyState title="Not available yet">
          Everyone’s picks become visible after picks lock. Check back once this week is closed.
        </EmptyState>
      </div>
    );
  }

  const rows = data.rows ?? [];
  const games = data.games ?? [];

  if (rows.length === 0) {
    return (
      <div>
        <SectionTitle>{data.week.displayName} Results</SectionTitle>
        <EmptyState title="No submissions">No one submitted picks for this week.</EmptyState>
      </div>
    );
  }

  const gameById = new Map(games.map((g) => [g.id, g]));

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <SectionTitle>{data.week.displayName} Results</SectionTitle>
          {isLive && (
            <span className="chip bg-danger/10 text-danger" title="Updating as games are scored">
              ● LIVE
            </span>
          )}
        </div>
        {data.week.calcStatus !== 'CALCULATED' && (
          <p className="-mt-2 text-sm text-muted">
            Games still in progress — placements update live and finalize once all games are scored.
          </p>
        )}
      </div>

      {/* Pick matrix (scrolls horizontally on small screens). */}
      <div className="card overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line bg-cream/60 text-xs uppercase tracking-wide text-muted">
              {/* Solid bg + z-index so scrolled game cells don't show through this frozen cell. */}
              <th className="sticky left-0 z-20 bg-cream px-3 py-2 text-left">Player</th>
              <th className="px-2 py-2 text-center">Place</th>
              <th className="px-2 py-2 text-center">Rank Pts</th>
              <th className="px-2 py-2 text-center">✓</th>
              {games.map((g) => (
                <th key={g.id} className="px-2 py-2 text-center" title={`${g.awayTeam.displayName} @ ${g.homeTeam.displayName}`}>
                  <div>{g.awayTeam.abbreviation}</div>
                  <div className="text-[10px] font-normal">@{g.homeTeam.abbreviation}</div>
                </th>
              ))}
              <th className="px-2 py-2 text-center">TB</th>
              <th className="px-2 py-2 text-center">Bonus</th>
              <th className="px-2 py-2 text-center">Final Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isMe = r.playerId === user?.id;
              const pickByGame = new Map(r.picks.map((p) => [p.gameId, p]));
              return (
                <tr key={r.playerId} className={`border-b border-line last:border-0 ${isMe ? 'bg-amber/10' : ''}`}>
                  {/* Solid bg + z-index so scrolled game cells don't show through this frozen cell.
                      #FCF7ED is bg-amber/10 composited over white — matches the isMe row tint opaquely. */}
                  <td className={`sticky left-0 z-10 px-3 py-2 font-medium ${isMe ? 'bg-[#FCF7ED]' : 'bg-surface'}`}>
                    {r.name}
                    {isMe && <span className="ml-1 chip bg-grove/10 text-grove">You</span>}
                  </td>
                  <td className="px-2 py-2 text-center font-display text-lg text-grove">
                    {ordinal(r.placement)}
                  </td>
                  <td className="px-2 py-2 text-center">{r.leaguePoints}</td>
                  <td className="px-2 py-2 text-center font-bold">
                    {r.correctPicks}/{games.length}
                  </td>
                  {games.map((g) => {
                    const pick = pickByGame.get(g.id);
                    const team =
                      pick && gameById.get(g.id)
                        ? pick.selectedTeamId === g.homeTeam.id
                          ? g.homeTeam.abbreviation
                          : g.awayTeam.abbreviation
                        : '—';
                    return (
                      <td key={g.id} className="px-2 py-2 text-center">
                        <PickCell abbr={team} correct={pick?.correct ?? null} />
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-center text-muted">{r.tiebreakerPrediction ?? '—'}</td>
                  <td className="px-2 py-2 text-center text-grove">
                    {r.bonusPoints ? `+${r.bonusPoints}` : '—'}
                  </td>
                  <td className="px-2 py-2 text-center font-bold">
                    {r.leaguePoints + r.bonusPoints}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted">
        <span>
          <CorrectDot correct /> correct pick
        </span>
        <span>
          <CorrectDot correct={false} /> incorrect pick
        </span>
        <span>
          <CorrectDot correct={null} /> tied / not yet started
        </span>
        {games.some((g) => g.isTiebreaker) && (
          <span>
            Actual tiebreaker total:{' '}
            <strong>{rows[0]?.actualTiebreaker ?? '—'}</strong>
          </span>
        )}
      </div>
    </div>
  );
}

function PickCell({ abbr, correct }: { abbr: string; correct: boolean | null }) {
  const cls =
    correct === true
      ? 'bg-success/15 text-success'
      : correct === false
        ? 'bg-danger/10 text-danger line-through'
        : 'bg-cream text-muted';
  const symbol = correct === true ? '✓' : correct === false ? '✗' : '·';
  return (
    <span className={`inline-flex min-w-[3rem] items-center justify-center gap-1 rounded px-1.5 py-1 font-medium ${cls}`}>
      <span aria-hidden>{symbol}</span>
      {abbr}
    </span>
  );
}

function CorrectDot({ correct }: { correct: boolean | null }) {
  const cls = correct === true ? 'text-success' : correct === false ? 'text-danger' : 'text-muted';
  const symbol = correct === true ? '✓' : correct === false ? '✗' : '·';
  return <span className={`font-bold ${cls}`}>{symbol}</span>;
}
